import {AfterViewInit, Component, OnInit, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {Observable, Observer} from 'rxjs';
import {RealEstateService} from '../../services/real-estate/real-estate.service';
import {AsyncPipe, NgClass, NgForOf, NgIf, NgTemplateOutlet} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {RealEstate, RealEstateCards} from '../../interfaces/real-estate';
import {RealEstateCardComponent} from '../real-estate-card/real-estate-card.component';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {initFlowbite} from 'flowbite';
import {NotificationModalComponent} from '../notification-modal/notification-modal.component';
import {GoogleMap, MapAdvancedMarker, MapInfoWindow} from '@angular/google-maps';
import {AuthenticationService} from '../../services/authentication/authentication.service';

type PriceRangeId = 'until-50k' | '50k-100k' | '100k-250k' | '250k-500k' | '500k-1m' | 'above-1m';
type AssessmentRangeId = 'until-100k' | '100k-250k' | '250k-500k' | '500k-1m' | 'above-1m';
type LegalTypeId = 'judicial' | 'extrajudicial';

@Component({
  selector: 'app-search',
  imports: [
    NgForOf,
    AsyncPipe,
    FormsModule,
    RealEstateCardComponent,
    NgClass,
    NotificationModalComponent,
    NgIf,
    NgTemplateOutlet,
    GoogleMap,
    MapAdvancedMarker,
    MapInfoWindow,
    RouterLink,
  ],
  templateUrl: './search.component.html',
  styleUrl: './search.component.css'
})
export class SearchComponent implements OnInit, AfterViewInit {
  // Notification modal properties
  showNotificationModal = false;

  // Pagination
  pageSize = 10;
  currentPage = 1;
  isPaginating = false;
  private paginationLoadingTimeout: ReturnType<typeof setTimeout> | null = null;

  // View mode: 'list' shows cards, 'map' shows Google Maps with markers
  viewMode: 'list' | 'map' = 'map';
  viewOrder: 'newer' | 'nearest-auction' | 'highest-discount' | 'lowest-price' | 'highest-price' = 'newer';
  isMobileFiltersOpen = false;
  showAllRealEstateTypes = false;
  selectedPriceRange: PriceRangeId | null = null;
  selectedAssessmentRange: AssessmentRangeId | null = null;
  pendingAssessmentRange: AssessmentRangeId | null = null;
  selectedLegalType: LegalTypeId | null = null;
  pendingLegalType: LegalTypeId | null = null;

  // Google Maps state for @angular/google-maps
  mapCenter: google.maps.LatLngLiteral = {lat: -14.2350, lng: -51.9253} as any;
  mapZoom = 4;
  selectedRealEstateForMap: RealEstateCards | null = null;
  searchAccessMessage = '';
  searchLoadErrorMessage = '';

  estates: string[] = [];
  cities: { city: string; file: string }[] = [];
  selectedEstate: string = '';
  selectedCity: { city: string; file: string } | null = null;
  minDiscount: number | null = null;
  appliedEstate: string = '';
  appliedCity: { city: string; file: string } | null = null;
  appliedMinDiscount: number | null = null;
  appliedPriceRange: PriceRangeId | null = null;
  appliedAssessmentRange: AssessmentRangeId | null = null;
  appliedLegalType: LegalTypeId | null = null;
  private appliedRealEstateTypes: Map<string, boolean> = new Map<string, boolean>();
  // Map to track selected real estate types
  selectedRealEstateTypes: Map<string, boolean> = new Map<string, boolean>();
  realEstates: Observable<RealEstateCards[]> | null = null;
  // Store the fetched real estates to avoid making new requests
  cachedRealEstates: RealEstateCards[] = [];
  // Store last filtered/sorted list for pagination and counts
  lastFilteredRealEstates: RealEstateCards[] = [];
  // Track which state-city combinations have been loaded
  private loadedStateCityCombinations: Map<string, boolean> = new Map<string, boolean>();

  // List of available real estate types
  readonly REAL_ESTATE_TYPES = [
    'Apartamento',
    'Casa',
    'Terreno',
    'Área rural',
    'Casa em condomínio',
    'Chácara',
    'Comercial',
    'Conjunto comercial',
    'Fazenda',
    'Galpão comercial',
    'Galpão industrial',
    'Galpão logístico',
    'Gleba',
    'Gleba de terras',
    'Hospital',
    'Hotel',
    'Ilha',
    'Imóvel industrial',
    'Laje comercial',
    'Lote',
    'Prédio comercial',
    'Prédio residencial',
    'Resort',
    'Sítio'
  ];

  readonly DEFAULT_VISIBLE_REAL_ESTATE_TYPES = ['Apartamento', 'Casa', 'Área rural'];
  readonly PRICE_RANGES: { id: PriceRangeId; label: string; min?: number; max?: number }[] = [
    { id: 'until-50k', label: 'Até R$ 50 mil', max: 50000 },
    { id: '50k-100k', label: 'R$ 50 mil - R$ 100 mil', min: 50000, max: 100000 },
    { id: '100k-250k', label: 'R$ 100 mil - R$ 250 mil', min: 100000, max: 250000 },
    { id: '250k-500k', label: 'R$ 250 mil - R$ 500 mil', min: 250000, max: 500000 },
    { id: '500k-1m', label: 'R$ 500 mil - R$ 1 milhão', min: 500000, max: 1000000 },
    { id: 'above-1m', label: 'Acima de R$ 1 milhão', min: 1000000 }
  ];
  readonly ASSESSMENT_RANGES: { id: AssessmentRangeId; label: string; min?: number; max?: number }[] = [
    { id: 'until-100k', label: 'Até R$ 100 mil', max: 100000 },
    { id: '100k-250k', label: 'R$ 100 mil - R$ 250 mil', min: 100000, max: 250000 },
    { id: '250k-500k', label: 'R$ 250 mil - R$ 500 mil', min: 250000, max: 500000 },
    { id: '500k-1m', label: 'R$ 500 mil - R$ 1 milhão', min: 500000, max: 1000000 },
    { id: 'above-1m', label: 'Acima de R$ 1 milhão', min: 1000000 }
  ];
  readonly LEGAL_TYPE_FILTERS: { id: LegalTypeId; label: string }[] = [
    { id: 'judicial', label: 'Judicial' },
    { id: 'extrajudicial', label: 'Extrajudicial' }
  ];
  private readonly DEFAULT_SEARCH_STATE = 'SP';
  private readonly DEFAULT_SEARCH_CITY = 'São Paulo';

  ngOnInit(): void {
    // Load states from the API via the service (which also caches states and their cities)
    this.realEstateService.getStates().then(states => {
      this.estates = states;
    }).catch(() => {
      this.estates = [];
    }).finally(() => {
      // Auto-apply filters and trigger search if URL contains parameters
      this.route.queryParams.subscribe(async (params) => {
        const estado = params['estado'];
        const cidade = params['cidade'];
        const discountParam = params['desconto'];
        const typesParam = params['types'] ?? params['type'] ?? params['tipo'];

        // New: view mode and order from URL
        const viewModeParam = params['viewMode'] ?? params['view_mode'] ?? params['visualizar'];
        const viewOrderParam = params['viewOrder'] ?? params['view_order'] ?? params['organizar'];

        // Apply view mode from URL if valid (do not navigate here to avoid loops)
        if (viewModeParam === 'list' || viewModeParam === 'map') {
          this.viewMode = viewModeParam;
        }

        // Apply view order from URL if valid (do not navigate here to avoid loops)
        if (viewOrderParam === 'newer' || viewOrderParam === 'nearest-auction' || viewOrderParam === 'highest-discount' || viewOrderParam === 'lowest-price' || viewOrderParam === 'highest-price') {
          this.viewOrder = viewOrderParam;
        }

        const searchState = estado || this.DEFAULT_SEARCH_STATE;
        const searchCity = cidade || this.DEFAULT_SEARCH_CITY;

        // Set state and load cities for that state
        if (searchState) {
          this.selectedEstate = searchState;
          try {
            const cities = await this.realEstateService.getCities(searchState);
            // Map to the expected shape (already in that shape from service)
            this.cities = cities as any;
            // Try to match city either by 'file' token or by display name
            if (searchCity) {
              const matched = this.cities.find(c => c.file === searchCity || c.city === searchCity) || null;
              this.selectedCity = matched;
            }
          } catch {
            this.cities = [];
          }
        }

        if (!estado || !cidade) {
          await this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
              estado: this.selectedEstate,
              cidade: this.selectedCity?.file ?? searchCity
            },
            queryParamsHandling: 'merge',
            replaceUrl: true
          });
          return;
        }

        // Discount filter
        if (typeof discountParam !== 'undefined') {
          const n = Number(discountParam);
          if (!isNaN(n)) {
            this.minDiscount = n;
          }
        }

        // Real estate types can be provided as comma separated string or repeated params
        if (typeof typesParam !== 'undefined') {
          let types: string[] = [];
          if (Array.isArray(typesParam)) {
            types = typesParam as string[];
          } else if (typeof typesParam === 'string') {
            types = typesParam.split(',').map(s => s.trim()).filter(Boolean);
          }
          types.forEach(t => this.selectedRealEstateTypes.set(t, true));
        }

        this.syncPendingFiltersFromSelectedFilters();
        this.applySelectedFiltersToSearch();
        await this.updateRealEstates();
      });
    });
  }

  ngAfterViewInit(): void {
    // Initialize Flowbite accordions after view is initialized
    initFlowbite();
    this.scrollToSearchAnchor('auto');

    this.mapMarkers?.changes.subscribe(() => {
      if (this.viewMode === 'map' && this.lastFilteredRealEstates.length > 0) {
        this.updateMapViewport(this.lastFilteredRealEstates);
      }
    });
  }

  setViewMode(mode: 'list' | 'map'): void {
    this.viewMode = mode;
    if (mode !== 'map') {
      this.selectedRealEstateForMap = null;
    } else {
      this.updateMapViewport(this.lastFilteredRealEstates);
    }
    // Sync to URL (merge with existing params)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        viewMode: this.viewMode
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  setViewOrder(order: 'newer' | 'nearest-auction' | 'highest-discount' | 'lowest-price' | 'highest-price'): void {
    this.viewOrder = order;
    // Reset to first page when order changes
    this.currentPage = 1;
    // Sync to URL (merge with existing params)
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        viewOrder: this.viewOrder
      },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
    // Re-apply sorting on the current dataset without refetching
    this.updateRealEstates();
  }

  openMobileFilters(): void {
    this.isMobileFiltersOpen = true;
  }

  closeMobileFilters(): void {
    this.isMobileFiltersOpen = false;
  }

  toggleRealEstateTypesVisibility(): void {
    this.showAllRealEstateTypes = !this.showAllRealEstateTypes;
  }

  get visibleRealEstateTypes(): string[] {
    return this.showAllRealEstateTypes ? this.REAL_ESTATE_TYPES : this.DEFAULT_VISIBLE_REAL_ESTATE_TYPES;
  }

  async applySearchFromFilters(): Promise<void> {
    this.applySelectedFiltersToSearch();
    await this.updateRealEstates();
    if (this.canSearch) {
      this.closeMobileFilters();
    }
  }

  private scrollToSearchAnchor(behavior: ScrollBehavior = 'smooth'): void {
    setTimeout(() => {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const selector = isDesktop ? '.search-page-anchor' : '.search-mobile-filters-anchor';
      const target = document.querySelector<HTMLElement>(selector);

      target?.scrollIntoView({ behavior, block: 'start' });
    }, 0);
  }

  private scrollToFiltersAnchor(behavior: ScrollBehavior = 'smooth'): void {
    setTimeout(() => {
      const isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      const selector = isDesktop ? '.search-desktop-filters-anchor' : '.search-mobile-filters-anchor';
      const target = document.querySelector<HTMLElement>(selector);

      target?.scrollIntoView({ behavior, block: 'start' });
    }, 0);
  }

  get activeFilterCount(): number {
    let count = 0;

    if (this.selectedEstate) {
      count += 1;
    }

    if (this.selectedCity) {
      count += 1;
    }

    if (this.minDiscount !== null && this.minDiscount > 0) {
      count += 1;
    }

    if (this.selectedPriceRange) {
      count += 1;
    }

    if (this.pendingAssessmentRange) {
      count += 1;
    }

    if (this.pendingLegalType) {
      count += 1;
    }

    for (const selected of this.selectedRealEstateTypes.values()) {
      if (selected) {
        count += 1;
      }
    }

    return count;
  }

  // Parse dates strictly in the format %Y-%m-%dT%H:%M:%SZ (UTC, trailing Z)
  private parseDate(value?: string): number | null {
    if (!value) return null;
    const isoZ = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
    if (!isoZ.test(value)) return null;

    try {
      const [datePart, timePartWithZ] = value.split('T');
      const [yearStr, monthStr, dayStr] = datePart.split('-');
      const timePart = timePartWithZ.replace('Z', '');
      const [hourStr, minuteStr, secondStr] = timePart.split(':');

      const year = Number(yearStr);
      const month = Number(monthStr) - 1; // Date.UTC month is 0-based
      const day = Number(dayStr);
      const hours = Number(hourStr);
      const minutes = Number(minuteStr);
      const seconds = Number(secondStr);

      const t = Date.UTC(year, month, day, hours, minutes, seconds);
      return isNaN(t) ? null : t;
    } catch {
      return null;
    }
  }

  // Parse dates in the format %Y-%m-%d (no time). Used for date_processed.
  private parseDateYYYYMMDD(value?: string): number | null {
    if (!value) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!m) return null;
    const year = Number(m[1]);
    const month = Number(m[2]) - 1; // 0-based
    const day = Number(m[3]);
    const t = Date.UTC(year, month, day, 0, 0, 0);
    return isNaN(t) ? null : t;
  }

  private getMaximumDiscount(property: RealEstateCards): number {
    return Math.max(
      property.auction_2_discount ?? 0
    );
  }

  private getMinimumBidPrice(property: RealEstateCards): number {
    const availablePrices = [
      property.auction_1_price,
      property.auction_2_price
    ].filter((price): price is number => typeof price === 'number' && !Number.isNaN(price));

    return availablePrices.length ? Math.min(...availablePrices) : Number.POSITIVE_INFINITY;
  }

  private getNearestUpcomingDate(re: RealEstateCards): number | null {
    const now = Date.now();
    const candidates: (number | null)[] = [
      this.parseDate(re.auction_1_start_date),
      this.parseDate(re.auction_2_start_date)
    ];
    // Keep only future dates
    const future = candidates.filter((t): t is number => t !== null && t >= now);
    if (future.length === 0) return null;
    return Math.min(...future);
  }

  private sortRealEstates(list: RealEstateCards[]): RealEstateCards[] {
    if (this.viewOrder === 'newer') {
      return [...list];
    }

    if (this.viewOrder === 'highest-discount') {
      return [...list].sort((a, b) => {
        const da = this.getMaximumDiscount(a);
        const db = this.getMaximumDiscount(b);
        return db - da; // highest discount first
      });
    }

    if (this.viewOrder === 'lowest-price' || this.viewOrder === 'highest-price') {
      const sorted = [...list].sort((a, b) => {
        const pa = this.getMinimumBidPrice(a);
        const pb = this.getMinimumBidPrice(b);
        return pa - pb; // ascending by default
      });
      return this.viewOrder === 'highest-price' ? sorted.reverse() : sorted;
    }

    // nearest-auction
    return [...list].sort((a, b) => {
      const na = this.getNearestUpcomingDate(a);
      const nb = this.getNearestUpcomingDate(b);
      if (na === null && nb === null) return 0;
      if (na === null) return 1; // a has no upcoming -> goes last
      if (nb === null) return -1; // b has no upcoming -> goes last
      return na - nb; // nearest (smaller timestamp) first
    });
  }

  @ViewChild(MapInfoWindow)
  infoWindow: MapInfoWindow | undefined;

  @ViewChild(GoogleMap)
  map: GoogleMap | undefined;

  @ViewChildren(MapAdvancedMarker)
  mapMarkers: QueryList<MapAdvancedMarker> | undefined;

  private pendingMapBounds: google.maps.LatLngBoundsLiteral | null = null;
  private mapViewportUpdateVersion = 0;

  onMapInitialized(): void {
    if (this.pendingMapBounds) {
      this.fitMapToBounds(this.pendingMapBounds, this.mapViewportUpdateVersion);
    }

    if (this.lastFilteredRealEstates.length > 0) {
      setTimeout(() => this.updateMapViewport(this.lastFilteredRealEstates), 0);
    }
  }

  openInfoWindow(infoWindow: MapInfoWindow, re: RealEstateCards, marker: MapAdvancedMarker): void {
    this.selectedRealEstateForMap = re;

    if (this.hasValidCoordinates(re)) {
      this.mapCenter = {
        lat: Number(re.lat),
        lng: Number(re.lng)
      } as any;
    }

    try {
      infoWindow.open(marker);
    } catch {
    }
  }

  // Close the map overlay card
  closeSelectedOnMap(): void {
    this.selectedRealEstateForMap = null;
  }

  constructor(
    private realEstateService: RealEstateService,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
  ) {
  }

  // Notification modal methods
  openNotificationModal(): void {
    this.showNotificationModal = true;
  }

  closeNotificationModal(): void {
    this.showNotificationModal = false;
  }

  onNotificationSaved(): void {
  }

  selectPriceRange(rangeId: PriceRangeId): void {
    this.selectedPriceRange = rangeId;
  }

  clearPriceRange(): void {
    this.selectedPriceRange = null;
  }

  selectAssessmentRange(rangeId: AssessmentRangeId): void {
    this.pendingAssessmentRange = rangeId;
  }

  clearAssessmentRange(): void {
    this.pendingAssessmentRange = null;
  }

  selectLegalType(typeId: LegalTypeId): void {
    this.pendingLegalType = typeId;
  }

  clearLegalType(): void {
    this.pendingLegalType = null;
  }

  private syncPendingFiltersFromSelectedFilters(): void {
    this.selectedAssessmentRange = this.pendingAssessmentRange;
    this.selectedLegalType = this.pendingLegalType;
  }

  private applySelectedFiltersToSearch(): void {
    this.appliedEstate = this.selectedEstate;
    this.appliedCity = this.selectedCity;
    this.appliedMinDiscount = this.minDiscount;
    this.appliedPriceRange = this.selectedPriceRange;
    this.appliedAssessmentRange = this.pendingAssessmentRange;
    this.appliedLegalType = this.pendingLegalType;
    this.appliedRealEstateTypes = new Map(this.selectedRealEstateTypes);

    this.selectedAssessmentRange = this.appliedAssessmentRange;
    this.selectedLegalType = this.appliedLegalType;
  }

  // Pagination helpers
  get totalItems(): number {
    return this.lastFilteredRealEstates.length;
  }

  get totalPages(): number {
    return this.totalItems > 0 ? Math.ceil(this.totalItems / this.pageSize) : 0;
  }

  get shownCount(): number {
    if (this.totalItems === 0) return 0;
    const end = this.currentPage * this.pageSize;
    return end > this.totalItems ? this.totalItems : end;
  }

  get pagesArray(): number[] {
    return this.totalPages > 0 ? Array.from({length: this.totalPages}, (_, i) => i + 1) : [];
  }

  // Compact paginator pages with ellipses
  get displayedPages(): (number | '…')[] {
    const total = this.totalPages;
    const current = this.currentPage;

    if (total === 0) return [];

    // If small number of pages, show all
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | '…')[] = [];
    const add = (p: number | '…') => {
      if (pages[pages.length - 1] !== p) pages.push(p);
    };

    const neighbors = 1; // show one page on each side of current
    const left = Math.max(2, current - neighbors);
    const right = Math.min(total - 1, current + neighbors);

    add(1);
    if (left > 2) add('…');
    for (let p = left; p <= right; p++) add(p);
    if (right < total - 1) add('…');
    add(total);

    return pages;
  }

  goToPage(page: number): void {
    if (this.totalPages === 0) return;
    const clamped = Math.min(Math.max(1, page), this.totalPages);
    if (clamped === this.currentPage) return;

    this.showPaginationLoadingState();
    this.currentPage = clamped;
    this.scrollToFiltersAnchor();
  }

  private showPaginationLoadingState(): void {
    this.isPaginating = true;

    if (this.paginationLoadingTimeout) {
      clearTimeout(this.paginationLoadingTimeout);
    }

    this.paginationLoadingTimeout = setTimeout(() => {
      this.isPaginating = false;
      this.paginationLoadingTimeout = null;
    }, 450);
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  getListRealEstates(realEstates: RealEstateCards[] | null): RealEstateCards[] {
    const list = realEstates ?? [];
    return list.slice((this.currentPage - 1) * this.pageSize, this.currentPage * this.pageSize);
  }

  getMapRealEstates(realEstates: RealEstateCards[] | null): (RealEstateCards & { lat: number; lng: number })[] {
    return (realEstates ?? [])
      .filter(re => this.hasValidCoordinates(re))
      .map(re => ({
        ...re,
        lat: Number(re.lat),
        lng: Number(re.lng),
      }));
  }

  getMarkerPosition(realEstate: RealEstateCards & { lat: number; lng: number }): google.maps.LatLngLiteral {
    return {
      lat: realEstate.lat,
      lng: realEstate.lng,
    };
  }

  private hasValidCoordinates(realEstate: RealEstateCards): realEstate is RealEstateCards & { lat: number; lng: number } {
    if (realEstate.lat === null || realEstate.lat === undefined || realEstate.lng === null || realEstate.lng === undefined) {
      return false;
    }

    const lat = Number(realEstate.lat);
    const lng = Number(realEstate.lng);

    return Number.isFinite(lat) && Number.isFinite(lng);
  }

  private updateMapViewport(realEstates: RealEstateCards[]): void {
    if (this.viewMode !== 'map') {
      return;
    }

    const realEstatesWithGeo = this.getMapRealEstates(realEstates);
    const viewportVersion = ++this.mapViewportUpdateVersion;

    if (realEstatesWithGeo.length === 0) {
      this.mapCenter = {lat: -14.2350, lng: -51.9253} as any;
      this.mapZoom = 4;
      return;
    }

    const lats = realEstatesWithGeo.map(realEstate => realEstate.lat);
    const lngs = realEstatesWithGeo.map(realEstate => realEstate.lng);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    this.mapCenter = {
      lat: (minLat + maxLat) / 2,
      lng: (minLng + maxLng) / 2
    } as any;

    if (realEstatesWithGeo.length === 1) {
      this.mapZoom = 14;
      return;
    }

    const latDelta = Math.max(maxLat - minLat, 0.01);
    const lngDelta = Math.max(maxLng - minLng, 0.01);
    const maxDeltaWithPadding = Math.max(latDelta, lngDelta) * 1.35;
    const zoom = Math.floor(Math.log2(360 / maxDeltaWithPadding)) + 1;

    this.mapZoom = Math.min(Math.max(zoom, 4), 15);
    const bounds: google.maps.LatLngBoundsLiteral = {
      south: minLat,
      west: minLng,
      north: maxLat,
      east: maxLng,
    };
    this.fitMapToBounds(bounds, viewportVersion);
  }

  private fitMapToBounds(bounds: google.maps.LatLngBoundsLiteral, viewportVersion: number): void {
    this.pendingMapBounds = bounds;
    let attempts = 0;
    const maxAttempts = 40;
    const retryDelayMs = 100;

    const applyBounds = () => {
      const googleMapInstance = this.map?.googleMap;

      if (this.viewMode !== 'map') {
        return;
      }

      if (viewportVersion !== this.mapViewportUpdateVersion) {
        return;
      }

      if (!googleMapInstance) {
        attempts += 1;
        if (attempts < maxAttempts) {
          setTimeout(applyBounds, retryDelayMs);
        }
        return;
      }

      googleMapInstance.fitBounds(bounds, 48);

      google.maps.event.addListenerOnce(googleMapInstance, 'idle', () => {
        if (viewportVersion !== this.mapViewportUpdateVersion) {
          return;
        }

        const center = googleMapInstance.getCenter();
        const zoom = googleMapInstance.getZoom();

        if (center) {
          this.mapCenter = {
            lat: center.lat(),
            lng: center.lng()
          } as any;
        }

        if (typeof zoom === 'number') {
          this.mapZoom = zoom;
        }
      });

      if (viewportVersion === this.mapViewportUpdateVersion) {
        this.pendingMapBounds = null;
      }
    };

    setTimeout(applyBounds, 0);
  }

  getSelectedType(): string | string[] | null {
    const selected: string[] = [];
    for (const [key, value] of this.appliedRealEstateTypes.entries()) {
      if (value) {
        selected.push(key);
      }
    }
    if (selected.length === 0) {
      return null;
    }
    if (selected.length === 1) {
      return selected[0];
    }
    return selected;
  }

  get canSearch(): boolean {
    return !!this.selectedEstate && !!this.selectedCity;
  }

  private get canRunAppliedSearch(): boolean {
    return !!this.appliedEstate && !!this.appliedCity;
  }

  get searchRequirementsMessage(): string {
    const missing: string[] = [];

    if (!this.selectedEstate) {
      missing.push('estado');
    }

    if (!this.selectedCity) {
      missing.push('cidade');
    }

    return missing.length ? `Selecione ${missing.join(', ')} para buscar.` : '';
  }


  updateCities() {
    // If the state changed, reset the selected city
    if (this.selectedEstate !== this.lastSelectedState) {
      this.selectedCity = null;
    }

    // Guard: if no state is selected, clear cities and exit
    if (!this.selectedEstate) {
      this.cities = [];
      return;
    }

    // Optionally clear current cities while loading new ones
    this.cities = [];

    this.realEstateService.getCities(this.selectedEstate).then(cities => {
      console.log('Cities loaded:', cities);
      // Map string[] to expected object[] shape if necessary
      const mapped: { city: string; file: string }[] = (cities as any[]).map((c: any) =>
        typeof c === 'string' ? { city: c, file: c } : { city: c.city, file: c.file }
      );
      console.log('Mapped cities:', mapped);
      this.cities = mapped;
      // Update last selected state after successfully loading cities
      this.lastSelectedState = this.selectedEstate;
    }).catch(() => {
      this.cities = [];
    });

    // Optionally, update URL or other side effects can go here
  }

  // Track the last selected state and city to detect changes
  private lastSelectedState: string = '';
  private lastSelectedCity: string  = '';

  async updateRealEstates() {
    if (!this.canRunAppliedSearch) {
      return;
    }

    this.searchAccessMessage = '';
    this.searchLoadErrorMessage = '';

    const currentUser = await this.authenticationService.getCurrentUser();
    const includeAuth = !!currentUser;

    console.log(`Estate: ${this.appliedEstate}`);
    console.log(`City: ${this.appliedCity}`);
    console.log('Min Discount:', this.appliedMinDiscount);
    // Always reset to first page on a new search/filter application
    this.currentPage = 1;

    // Create a key for the current state-city combination
    const stateCityKey = this.getStateCityCacheKey(this.appliedEstate, this.appliedCity ? this.appliedCity.file : '', includeAuth);

    // Check if we need to fetch new data
    const alreadyLoaded = this.loadedStateCityCombinations.get(stateCityKey) === true;
    const needToFetchData = !alreadyLoaded;

    // console.log(`State-City: ${stateCityKey}, Already loaded: ${alreadyLoaded}, Need to fetch: ${needToFetchData}`);
    //
    // console.log(this.selectedCity);

    if (needToFetchData) {
      // Get real estates by state and city (city is a string now)
      const cityFile = this.appliedCity ? this.appliedCity.file : '';
      const realEstatesObservable = this.realEstateService.getRealEstatesByCity(
        this.appliedEstate,
        cityFile,
        includeAuth
      );

      // Store the data and apply filters
      this.realEstates = new Observable<RealEstateCards[]>(observer => {
        realEstatesObservable.subscribe({
          next: (realEstates) => {
            this.handleFetchedRealEstates(realEstates, observer, includeAuth);
          },
          error: (error) => {
            if (this.isAccessError(error)) {
              this.realEstateService.getRealEstatesByCity(this.appliedEstate, cityFile, false).subscribe({
                next: (realEstates) => {
                  this.handleFetchedRealEstates(realEstates, observer, false);
                },
                error: () => {
                  this.realEstateService.getExampleRealEstates().subscribe({
                    next: (exampleRealEstates) => {
                      this.handleFetchedRealEstates(this.buildGuestPreviewRealEstates(exampleRealEstates), observer, false);
                    },
                    error: () => {
                      this.searchLoadErrorMessage = 'A busca pública ainda não está disponível na API. Tente novamente em instantes.';
                      observer.next([]);
                      observer.complete();
                    }
                  });
                }
              });
              return;
            }
            observer.error(error);
          }
        });
      });
    } else {
      // Just filter the cached data without making a new request
      this.realEstates = new Observable<RealEstateCards[]>(observer => {
        let filteredRealEstates = this.cachedRealEstates;

        // Apply discount filter if needed
        if (this.appliedMinDiscount !== null && this.appliedMinDiscount > 0) {
          filteredRealEstates = this.filterByDiscount(filteredRealEstates);
        }

        // Apply price range filter if selected
        if (this.appliedPriceRange) {
          filteredRealEstates = this.filterByPriceRange(filteredRealEstates);
        }

        // Apply assessment value range filter if selected
        if (this.appliedAssessmentRange) {
          filteredRealEstates = this.filterByAssessmentRange(filteredRealEstates);
        }

        // Apply legal type filter if selected
        if (this.appliedLegalType) {
          filteredRealEstates = this.filterByLegalType(filteredRealEstates);
        }

        // Apply real estate type filter if any types are selected
        if (this.hasSelectedRealEstateTypes()) {
          filteredRealEstates = this.filterByRealEstateType(filteredRealEstates);
        }

        // Apply ordering
        filteredRealEstates = this.sortRealEstates(filteredRealEstates);

        // Save for pagination/counts
        this.lastFilteredRealEstates = filteredRealEstates;

        observer.next(filteredRealEstates);
        this.scrollToSearchAnchor();

        // Reinitialize Flowbite after DOM updates
        setTimeout(() => {
          initFlowbite();
        }, 0);

        this.updateMapViewport(filteredRealEstates);
      });
    }

    // // Update URL with both selected estate and city
    // this.router.navigate([], {
    //   relativeTo: this.route,
    //   queryParams: {
    //     estado: this.selectedEstate,
    //     cidade: this.selectedCity
    //   },
    //   queryParamsHandling: 'merge'
    // });
  }

  private showSearchAccessMessage(): void {
    this.searchAccessMessage = 'Para ver os resultados da busca, entre na sua conta ou escolha uma assinatura da Casato.';
    this.searchLoadErrorMessage = '';
    this.realEstates = null;
    this.lastFilteredRealEstates = [];
    this.selectedRealEstateForMap = null;
  }

  private getStateCityCacheKey(state: string, cityFile: string, includeAuth: boolean): string {
    return `${state}-${cityFile}-${includeAuth ? 'auth' : 'public'}`;
  }

  private handleFetchedRealEstates(realEstates: RealEstateCards[], observer: Observer<RealEstateCards[]>, includeAuth: boolean): void {
    this.cachedRealEstates = realEstates;

    const stateCityKey = this.getStateCityCacheKey(this.appliedEstate, this.appliedCity ? this.appliedCity.file : '', includeAuth);
    this.loadedStateCityCombinations.set(stateCityKey, true);

    this.lastSelectedState = this.appliedEstate;
    this.lastSelectedCity = this.appliedCity ? this.appliedCity.file : '';

    const filteredRealEstates = this.getFilteredRealEstates(this.cachedRealEstates);
    this.publishRealEstateResults(filteredRealEstates, observer);
  }

  private buildGuestPreviewRealEstates(realEstates: RealEstate[]): RealEstateCards[] {
    if (realEstates.length === 0) {
      return [];
    }

    const previewRealEstates: RealEstateCards[] = [];

    for (let index = 0; index < 10; index += 1) {
      const realEstate = realEstates[index % realEstates.length];
      previewRealEstates.push({
        ...realEstate,
        link: `${realEstate.link}#preview-${index}`
      });
    }

    return previewRealEstates;
  }

  private getFilteredRealEstates(realEstates: RealEstateCards[]): RealEstateCards[] {
    let filteredRealEstates = realEstates;

    if (this.appliedMinDiscount !== null && this.appliedMinDiscount > 0) {
      filteredRealEstates = this.filterByDiscount(filteredRealEstates);
    }

    if (this.appliedPriceRange) {
      filteredRealEstates = this.filterByPriceRange(filteredRealEstates);
    }

    if (this.appliedAssessmentRange) {
      filteredRealEstates = this.filterByAssessmentRange(filteredRealEstates);
    }

    if (this.appliedLegalType) {
      filteredRealEstates = this.filterByLegalType(filteredRealEstates);
    }

    if (this.hasSelectedRealEstateTypes()) {
      filteredRealEstates = this.filterByRealEstateType(filteredRealEstates);
    }

    return this.sortRealEstates(filteredRealEstates);
  }

  private publishRealEstateResults(realEstates: RealEstateCards[], observer: Observer<RealEstateCards[]>): void {
    this.lastFilteredRealEstates = realEstates;

    observer.next(realEstates);
    this.scrollToSearchAnchor();

    setTimeout(() => {
      initFlowbite();
    }, 0);

    this.updateMapViewport(realEstates);
  }

  private isAccessError(error: any): boolean {
    return error?.status === 401 || error?.status === 403;
  }

  // Helper method to filter real estates by discount
  private filterByDiscount(realEstates: RealEstateCards[]): RealEstateCards[] {
    return realEstates.filter(property => {
      const auction2Discount = property.auction_2_discount || 0;
      return auction2Discount >= (this.appliedMinDiscount || 0);
    });
  }

  private filterByPriceRange(realEstates: RealEstateCards[]): RealEstateCards[] {
    const selectedRange = this.PRICE_RANGES.find(range => range.id === this.appliedPriceRange);

    if (!selectedRange) {
      return realEstates;
    }

    return realEstates.filter(property => {
      const price = this.getMinimumBidPrice(property);

      if (!Number.isFinite(price)) {
        return false;
      }

      if (typeof selectedRange.min === 'number' && price <= selectedRange.min) {
        return false;
      }

      if (typeof selectedRange.max === 'number' && price > selectedRange.max) {
        return false;
      }

      return true;
    });
  }

  private filterByAssessmentRange(realEstates: RealEstateCards[]): RealEstateCards[] {
    const selectedRange = this.ASSESSMENT_RANGES.find(range => range.id === this.appliedAssessmentRange);

    if (!selectedRange) {
      return realEstates;
    }

    return realEstates.filter(property => {
      const assessmentValue = property.assessment_price;

      if (assessmentValue === null || typeof assessmentValue === 'undefined') {
        return false;
      }

      if (typeof selectedRange.min === 'number' && assessmentValue <= selectedRange.min) {
        return false;
      }

      if (typeof selectedRange.max === 'number' && assessmentValue > selectedRange.max) {
        return false;
      }

      return true;
    });
  }

  private filterByLegalType(realEstates: RealEstateCards[]): RealEstateCards[] {
    return realEstates;
  }

  private matchesLegalTypeFilter(property: RealEstate, filterId: LegalTypeId): boolean {
    const auctionType = this.normalizeFilterText(property.auction_type);

    if (filterId === 'extrajudicial') {
      return auctionType.includes('extrajudicial');
    }

    return auctionType.includes('judicial') && !auctionType.includes('extrajudicial');
  }

  private normalizeFilterText(value?: string | null): string {
    return (value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('pt-BR');
  }


  // Helper method to check if any real estate types are selected
  hasSelectedRealEstateTypes(): boolean {
    for (const selected of this.appliedRealEstateTypes.values()) {
      if (selected) {
        return true;
      }
    }
    return false;
  }

  // Helper method to filter real estates by type
  private filterByRealEstateType(realEstates: RealEstateCards[]): RealEstateCards[] {
    // If no real estate types are selected, return all real estates
    if (!this.hasSelectedRealEstateTypes()) {
      return realEstates;
    }

    const selectedTypes = new Set(
      Array.from(this.appliedRealEstateTypes.entries())
        .filter(([, selected]) => selected)
        .map(([type]) => type.toLocaleLowerCase('pt-BR'))
    );

    return realEstates.filter(property => {
      // If this property type is selected, include it
      return selectedTypes.has(property.real_estate_type.toLocaleLowerCase('pt-BR'));
    });
  }

  // Toggle real estate type selection without updating results
  toggleRealEstateType(type: string, event: any): void {
    const isChecked = event.target.checked;
    this.selectedRealEstateTypes.set(type, isChecked);
    // Removed immediate filtering - will be applied when "Buscar agora" is clicked
  }

  // Clear all filters except state and city and reset to default state
  clearFilters(): void {
    console.log('Clearing filters without reloading data');

    // Preserve state and city selections
    // Store current state and city values
    const currentState = this.selectedEstate;
    const currentCity = this.selectedCity;

    // Reset discount filter
    this.minDiscount = null;

    // Reset price filter
    this.selectedPriceRange = null;

    // Reset assessment value filter
    this.selectedAssessmentRange = null;
    this.pendingAssessmentRange = null;

    // Reset legal type filter
    this.selectedLegalType = null;
    this.pendingLegalType = null;

    // Reset real estate type filters
    this.selectedRealEstateTypes.clear();

    this.selectedEstate = currentState;
    this.selectedCity = currentCity;
  }
}
